/**
 * Reversible per-anomaly ASSESSMENT control (#331).
 *
 * The anomaly queue's operator "confirm/dismiss" was divergent per family and, worse,
 * ONE-WAY — a mis-clicked Dismiss was UNRECOVERABLE in-COP. This control is the single
 * workstate-native, REVERSIBLE assessment axis: unconfirmed ⇄ confirmed ⇄ false_positive.
 *
 * It is DELIBERATELY DISTINCT from two neighbours it must never be confused with:
 *   • the ✓ Ack (AnomalyAckButton) — a triage DEMOTE, orthogonal to the disposition; and
 *   • the R.3 "Confirm Wildfire" DISPATCH promotion (DispatchActions) — which raises a
 *     dispatch recommendation. This control NEVER raises a dispatch recommendation: it
 *     only PUTs anomaly-workstate. The verbs (confirm_anomaly / reopen_anomaly /
 *     dismiss_anomaly) and the "Assessment" caption keep the surfaces visibly separate.
 *
 * Every transition is audited APPEND-ONLY server-side (atomic in the PUT transaction), so
 * this control does NOT log its own operator_actions row — the server owns the immutable
 * ledger; history is never lost. On flood/ice a false_positive ALSO mirrors the source
 * status to 'dismissed' so the #249 re-alarm override re-raises an escalating dismissed
 * anomaly (handled server-side; see routes/anomaly-workstate.ts).
 *
 * Reopen (not "Unconfirm" — codebase grammar) reverses either resolved state back to
 * unconfirmed. "Confirm"/"Reopen"/"Dismiss"/"False positive" are neutral plain-language
 * handles that collide with no reserved Ontario IMS term (Declare / release / demobilize).
 */
import { useEffect, useState } from 'react';
import { putAnomalyWorkstate, type AnomalyAssessment, type AnomalyWorkstateSource } from './api';
import { getSessionId } from './lib/operatorActions';
import { broadcastWorkstate, useWorkstateBridge } from './workstateBridge';

export function AnomalyAssessmentControl({
  source,
  sourceId,
  initialAssessment = 'unconfirmed',
  onAssessed,
}: {
  source: AnomalyWorkstateSource;
  /** Raw surface uuid — the workstate write key (anomaly.id). */
  sourceId: string;
  /** Server-persisted current assessment (rides the queue feature properties). */
  initialAssessment?: AnomalyAssessment;
  /** Optional hook after a successful transition (e.g. nudge a queue refresh). */
  onAssessed?: (assessment: AnomalyAssessment) => void;
}): JSX.Element {
  const [assessment, setAssessment] = useState<AnomalyAssessment>(initialAssessment);
  const [busy, setBusy] = useState(false);

  // Resync to server truth when the selected item changes or its persisted
  // assessment flips on a refetch.
  useEffect(() => {
    setAssessment(initialAssessment);
  }, [source, sourceId, initialAssessment]);

  // Cross-window (#264 idiom): a sibling window assessing THIS exact item reflects here.
  useWorkstateBridge((msg) => {
    if (msg.sourceType === source && msg.sourceId === sourceId && msg.assessment) {
      setAssessment(msg.assessment);
    }
  });

  const write = async (next: AnomalyAssessment): Promise<void> => {
    if (busy || next === assessment) return;
    const prev = assessment;
    setBusy(true);
    setAssessment(next); // optimistic
    try {
      // The server emits the append-only audit row (atomic with the write) — the client
      // must NOT also log it, or the immutable ledger would double-count the transition.
      const res = await putAnomalyWorkstate(source, sourceId, { assessment: next }, getSessionId());
      setAssessment(res.assessment ?? next);
      broadcastWorkstate(res);
      onAssessed?.(next);
    } catch (err) {
      setAssessment(prev); // revert so the operator can retry
      console.error('assessment persist failed', err);
    } finally {
      setBusy(false);
    }
  };

  const CHIP: Record<AnomalyAssessment, { label: string; bg: string; fg: string; title: string }> =
    {
      unconfirmed: {
        label: 'Unconfirmed',
        bg: '#64748b22',
        fg: '#94a3b8',
        title: 'Not yet assessed by an operator.',
      },
      confirmed: {
        label: '✓ Confirmed',
        bg: '#05966922',
        fg: '#34d399',
        title: 'Operator-confirmed as a real event (does NOT raise a dispatch recommendation).',
      },
      false_positive: {
        label: '✕ False positive',
        bg: '#b91c1c22',
        fg: '#f87171',
        title:
          'Dismissed as a false positive — reversible. On flood/ice this still re-alarms if it escalates.',
      },
    };

  return (
    <div data-testid="anomaly-assessment" className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Assessment
        </span>
        <span
          data-testid="anomaly-assessment-state"
          className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium"
          style={{ backgroundColor: CHIP[assessment].bg, color: CHIP[assessment].fg }}
          title={CHIP[assessment].title}
        >
          {CHIP[assessment].label}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {assessment === 'unconfirmed' ? (
          <>
            <button
              type="button"
              data-testid="anomaly-assessment-confirm"
              disabled={busy}
              onClick={() => void write('confirmed')}
              className="inline-flex items-center gap-1 rounded bg-emerald-600/80 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              title="Confirm this anomaly is a real event (no dispatch recommendation is raised)."
            >
              {busy ? 'Saving…' : 'Confirm'}
            </button>
            <button
              type="button"
              data-testid="anomaly-assessment-dismiss"
              disabled={busy}
              onClick={() => void write('false_positive')}
              className="inline-flex items-center gap-1 rounded border border-slate-500 bg-slate-700/60 px-3 py-1.5 text-[13px] font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50"
              title="Mark false positive — reversible. On flood/ice it re-alarms if the hazard escalates."
            >
              {busy ? 'Saving…' : 'Mark false positive'}
            </button>
          </>
        ) : (
          <button
            type="button"
            data-testid="anomaly-assessment-reopen"
            disabled={busy}
            onClick={() => void write('unconfirmed')}
            className="inline-flex items-center gap-1 rounded border border-amber-500 bg-amber-600/20 px-3 py-1.5 text-[13px] font-medium text-amber-300 hover:bg-amber-600/30 disabled:opacity-50"
            title="Reopen — reverse this assessment back to unconfirmed."
          >
            {busy ? 'Saving…' : 'Reopen'}
          </button>
        )}
      </div>
    </div>
  );
}
